import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
  visuallyHidden,
} from '../styles';
import { Container } from './ui';
import PardotForm from './PardotForm';

const StyledContactForm = styled.section`
  ${sectionPaddings()};

  ${({ altBackground }) => {
    if (altBackground) {
      return css`
        color: ${standardColours.white};
        background-color: ${brandColours.quaternary};
      `;
    } else {
      return css`
        background-color: ${brandColours.primary};
      `;
    }
  }}
`;

const StyledInner = styled.div`
  display: grid;
  gap: 30px;

  ${minBreakpointQuery.small`
     gap: 40px;
  `}

  ${minBreakpointQuery.smedium`
    grid-template-columns: 0.45fr 0.55fr;
    gap: 60px;
  `}

  ${minBreakpointQuery.medium`
    gap: 80px;
  `}

  ${minBreakpointQuery.mlarge`
    gap: 100px;
  `}

  ${minBreakpointQuery.large`
    gap: 120px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 140px;
  `}
`;

const StyledHeader = styled.header`
  ${minBreakpointQuery.smedium`
    display: flex;
    flex-direction: column;
  `}
`;

const StyledHeading = styled.h2`
  ${fluidFontSize(
    '25px',
    '45px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};

  ${({ altBackground }) => {
    if (!altBackground) {
      return css`
        color: ${brandColours.secondary};
      `;
    }
  }}
`;

const StyledText = styled.p`
  margin-top: 12px;
  line-height: 1.7;
  white-space: break-spaces;

  ${minBreakpointQuery.small`
    margin-top: 15px;
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.large`
    margin-top: 18px;
    ${fontSize(20)};
  `}
`;

const StyledImage = styled.img`
  ${maxBreakpointQuery.smedium`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.smedium`
    margin-top: auto;
    margin-bottom: auto;
    padding-top: 30px;
    max-height: 320px;
  `}
`;

const ContactForm = ({
  heading: headingOverride,
  text: textOverride,
  image: imageOverride,
  pardotFormUrl: pardotFormUrlOverride,
  pardotFormHeight: pardotFormHeightOverride,
  altBackground,
  isContactOrCallbackPage,
}) => {
  const {
    datoCmsContactFormSitewide: {
      heading: initialHeading,
      text: initialText,
      image: initialImage,
      pardotFormUrl: initialPardotFormUrl,
      pardotFormHeight: initialPardotFormHeight,
    },
  } = useStaticQuery(graphql`
    query ContactFormQuery {
      datoCmsContactFormSitewide {
        heading
        text
        image {
          url
          alt
        }
        pardotFormUrl
        pardotFormHeight
      }
    }
  `);

  const heading = headingOverride ? headingOverride : initialHeading;
  const text = textOverride ? textOverride : initialText;
  const image = imageOverride ? imageOverride : initialImage;
  const pardotFormUrl = pardotFormUrlOverride
    ? pardotFormUrlOverride
    : initialPardotFormUrl;
  const pardotFormHeight = pardotFormHeightOverride
    ? pardotFormHeightOverride
    : initialPardotFormHeight;

  return (
    <StyledContactForm altBackground={altBackground}>
      <Container>
        <StyledInner>
          <StyledHeader>
            <StyledHeading
              as={isContactOrCallbackPage ? 'h1' : 'h2'}
              altBackground={altBackground}
            >
              {heading}
            </StyledHeading>
            <StyledText>{text}</StyledText>
            <StyledImage src={image.url} alt={image.alt} loading="lazy" />
          </StyledHeader>
          <PardotForm url={pardotFormUrl} height={pardotFormHeight} />
        </StyledInner>
      </Container>
    </StyledContactForm>
  );
};

export default ContactForm;
